/**
 * Photo Upload Component - Inline Version
 *
 * Handles photo uploads for custom photo printing products.
 * Opens native file picker directly from the configurator row.
 * Integrates with the mock image system and cart form.
 */


class PhotoUploadComponent extends HTMLElement {
  constructor() {
    super();
    this.uploadedFile = null;
    this.uploadedImageURL = null;
    this.croppedImageURL = null;
    this.originalImageData = null;
    this.maxSizeMB = 10;
    this.errorTimeout = null;
  }

  connectedCallback() {
    this.maxSizeMB = parseInt(this.dataset.maxSizeMb) || 10;

    // Get localized error messages from data attributes
    this.errorMessages = {
      invalidType: this.dataset.errorInvalidType || 'Please upload a valid photo (JPG, PNG, WEBP, or HEIC)',
      fileSize: this.dataset.errorFileSize || `Photo must be less than ${this.maxSizeMB}MB`,
      heicFailed: this.dataset.errorHeicFailed || 'HEIC photos couldn\'t be converted automatically. On iPhone, go to Settings → Camera → Formats and choose "Most Compatible" to shoot in JPEG, then try again.',
      lowResolution: this.dataset.errorLowResolution || 'Photo resolution is too low. Minimum {min_mp} required. Your photo is {your_mp} ({width}×{height}px).'
    };

    // Get localized labels
    this.labels = {
      upload: this.dataset.labelUpload || 'Upload your photo',
      uploadSublabel: this.dataset.labelUploadSublabel || 'Add your photo to see it framed',
      processing: this.dataset.labelProcessing || 'Processing...',
      yourPhoto: this.dataset.labelYourPhoto || 'Your photo',
      change: this.dataset.labelChange || 'Click to change'
    };

    // Get UI elements
    this.fileInput = this.querySelector('[data-photo-input]');
    this.removeButton = this.querySelector('[data-remove-photo]');
    this.fileNameEl = this.querySelector('[data-file-name]');
    this.uploadedSubtitleEl = this.querySelector('[data-uploaded-subtitle]');
    this.progressFill = this.querySelector('[data-progress-fill]');

    // State elements
    this.defaultState = this.querySelector('[data-upload-state="default"]');
    this.processingState = this.querySelector('[data-upload-state="processing"]');
    this.uploadedState = this.querySelector('[data-upload-state="uploaded"]');

    // Error tooltip elements
    this.errorTooltip = this.querySelector('[data-error-tooltip]');
    this.errorMessage = this.querySelector('[data-error-message]');
    this.errorClose = this.querySelector('[data-error-close]');

    this.#setupEventListeners();
    this.#restoreUploadedPhoto();
  }

  disconnectedCallback() {
    if (this.uploadedImageURL) {
      URL.revokeObjectURL(this.uploadedImageURL);
    }
    if (this.croppedImageURL) {
      URL.revokeObjectURL(this.croppedImageURL);
    }
    if (this.errorTimeout) {
      clearTimeout(this.errorTimeout);
    }
  }

  #setupEventListeners() {

    // File input change
    this.fileInput?.addEventListener('change', (e) => {
      this.#handleFileSelect(e);
    });

    // Click on the default state button to trigger file input
    this.defaultState?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (this.fileInput) {
        this.fileInput.click();
      } else {
        console.error('[PhotoUpload] File input not found!');
      }
    });

    // Click on the uploaded state button to trigger file input for changing photo
    const uploadedLabel = this.uploadedState?.querySelector('.photo-upload-row__uploaded-content');
    uploadedLabel?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (this.fileInput) {
        this.fileInput.click();
      }
    });

    // Remove button
    this.removeButton?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.#removePhoto();
    });

    // Error tooltip close button
    this.errorClose?.addEventListener('click', () => this.#hideError());

    // Click outside error tooltip to close it
    document.addEventListener('click', (e) => {
      if (this.errorTooltip && !this.errorTooltip.hasAttribute('hidden')) {
        // Close if click is outside the tooltip
        if (!this.errorTooltip.contains(e.target)) {
          this.#hideError();
        }
      }
    });

    // Listen for size option changes to re-crop the image
    document.addEventListener('sku-picker:selection-changed', (e) => this.#handleSizeChange(e));
  }

  #handleFileSelect(event) {
    const file = event.target.files?.[0];
    if (file) {
      this.#processFile(file);
    }
  }

  async #processFile(file) {
    // Hide previous errors
    this.#hideError();

    // Show processing state
    this.#setState('processing');
    this.#updateProgress(0);

    // Validate file type — also check extension as fallback since some browsers
    // report HEIC files as application/octet-stream or empty string
    const isHeicByExt = /\.(heic|heif)$/i.test(file.name);
    const effectiveType = file.type || (isHeicByExt ? 'image/heic' : '');
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
    if (!validTypes.includes(effectiveType)) {
      this.#showError(this.errorMessages.invalidType);
      this.#setState('default');
      return;
    }

    // Validate file size
    const maxSizeBytes = this.maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      this.#showError(this.errorMessages.fileSize);
      this.#setState('default');
      return;
    }

    this.#updateProgress(10);

    // Convert HEIC to JPEG if needed
    let processedFile = file;
    if (effectiveType === 'image/heic' || effectiveType === 'image/heif') {
      try {
        processedFile = await this.#convertHeicToJpeg(file);
        this.#updateProgress(30);
      } catch (error) {
        console.error('[PhotoUpload] HEIC conversion failed:', error);
        this.#showError(this.errorMessages.heicFailed);
        this.#setState('default');
        return;
      }
    }

    // Store file
    this.uploadedFile = processedFile;

    // Load image and store original data
    try {
      await this.#loadAndStoreImage(processedFile);
      this.#updateProgress(50);
    } catch (error) {
      console.error('[PhotoUpload] Failed to load image:', error);
      this.#showError('Failed to load photo. Please try again.');
      this.#setState('default');
      return;
    }

    // Validate minimum resolution based on smallest print size at 150 DPI
    // Smallest size is typically 9"x12" = 1350x1800px at 150 DPI = 2.43 MP
    const minDPI = 150;
    const minWidthInches = parseFloat(this.dataset.minWidthInches) || 9;
    const minHeightInches = parseFloat(this.dataset.minHeightInches) || 12;
    const minPixelsRequired = (minWidthInches * minDPI) * (minHeightInches * minDPI);
    const photoPixels = this.originalImageData.width * this.originalImageData.height;

    if (photoPixels < minPixelsRequired) {
      const yourMegapixels = this.#formatMegapixels(photoPixels);
      const warnMsg = `Your photo is ${yourMegapixels} (${this.originalImageData.width}×${this.originalImageData.height}px) — it may not be perfectly sharp at larger print sizes, but we'll do our best. You can continue or upload a higher-resolution photo.`;
      this.#showWarning(warnMsg);
    }

    this.#updateProgress(70);

    // Crop image to current aspect ratio
    await this.#cropToCurrentAspectRatio();
    this.#updateProgress(90);

    // Create blob URL for preview
    if (!this.uploadedImageURL) {
      this.uploadedImageURL = URL.createObjectURL(processedFile);
    }

    // Update form with photo
    this.#updateFormProperty(processedFile);

    // Update uploaded state display
    this.#updateUploadedDisplay();

    this.#updateProgress(100);

    // Small delay to show 100% before switching state
    await new Promise(resolve => setTimeout(resolve, 200));

    // Show uploaded state
    this.#setState('uploaded');

    // Notify gallery to update mocks
    this.#notifyGallery();

    // Enable add to cart button
    this.#enableAddToCartButton();
  }

  #setState(state) {
    // Hide all states
    this.defaultState?.setAttribute('hidden', '');
    this.processingState?.setAttribute('hidden', '');
    this.uploadedState?.setAttribute('hidden', '');

    // Show requested state
    switch (state) {
      case 'default':
        this.defaultState?.removeAttribute('hidden');
        break;
      case 'processing':
        this.processingState?.removeAttribute('hidden');
        break;
      case 'uploaded':
        this.uploadedState?.removeAttribute('hidden');
        break;
    }
  }

  #updateProgress(percent) {
    if (this.progressFill) {
      this.progressFill.style.width = `${percent}%`;
    }
  }

  #updateUploadedDisplay() {
    // Set title to "Your photo"
    if (this.fileNameEl) {
      this.fileNameEl.textContent = this.labels.yourPhoto;
    }

    // Set subtitle to "X.XMP · Click to change"
    if (this.uploadedSubtitleEl && this.originalImageData) {
      const mp = this.#formatMegapixels(this.originalImageData.width * this.originalImageData.height);
      this.uploadedSubtitleEl.textContent = `${mp} · ${this.labels.change}`;
    }
  }

  #removePhoto() {
    // Clean up blob URLs
    if (this.uploadedImageURL) {
      URL.revokeObjectURL(this.uploadedImageURL);
      this.uploadedImageURL = null;
    }
    if (this.croppedImageURL) {
      URL.revokeObjectURL(this.croppedImageURL);
      this.croppedImageURL = null;
    }

    // Clear stored data
    this.uploadedFile = null;
    this.originalImageData = null;
    this._lastSizeOption = null;

    // Drop the cached S3 upload (QA #108) so a fresh photo re-uploads.
    this._s3Promise = null;
    this._s3ImageUrl = null;

    // Clear sessionStorage
    sessionStorage.removeItem('uploaded_photo');

    // Reset file input
    if (this.fileInput) {
      this.fileInput.value = '';
    }

    // Show default state
    this.#setState('default');

    // Remove form property
    this.#removeFormProperty();

    // Notify gallery to revert to original artwork
    this.#notifyGalleryRemoved();

    // Disable add to cart button
    this.#disableAddToCartButton();
  }

  #showError(message) {
    if (this.errorMessage) {
      this.errorMessage.textContent = message;
    }
    this.errorTooltip?.removeAttribute('hidden');

    // Reset file input so the same file can be re-selected
    if (this.fileInput) {
      this.fileInput.value = '';
    }
  }

  #hideError() {
    this.errorTooltip?.setAttribute('hidden', '');
    if (this.errorTimeout) {
      clearTimeout(this.errorTimeout);
      this.errorTimeout = null;
    }
  }

  #showWarning(message) {
    if (this.errorMessage) {
      this.errorMessage.textContent = message;
    }
    if (this.errorTooltip) {
      this.errorTooltip.removeAttribute('hidden');
      this.errorTooltip.dataset.severity = 'warning';
      this.errorTimeout = setTimeout(() => this.#hideError(), 8000);
    }
  }

  #updateFormProperty(file) {
    const form = document.querySelector('form[action*="/cart/add"]');
    if (!form) {
      console.warn('[PhotoUpload] Cart form not found');
      return;
    }

    // Remove existing property input if present
    const existingInput = form.querySelector('input[name="properties[_uploaded_photo_url]"]');
    if (existingInput) {
      existingInput.remove();
    }

    // Create hidden input with blob URL
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'properties[_uploaded_photo_url]';
    input.value = this.uploadedImageURL;
    input.setAttribute('data-photo-upload-property', '');
    form.appendChild(input);

    // Also store file name for reference
    const nameInput = document.createElement('input');
    nameInput.type = 'hidden';
    nameInput.name = 'properties[Photo]';
    nameInput.value = file.name;
    nameInput.setAttribute('data-photo-upload-property', '');
    form.appendChild(nameInput);
  }

  #removeFormProperty() {
    const form = document.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    const properties = form.querySelectorAll('[data-photo-upload-property]');
    properties.forEach(prop => prop.remove());
  }

  #notifyGallery() {
    const imageUrl = this.croppedImageURL || this.uploadedImageURL;
    if (!imageUrl) {
      console.warn('[PhotoUpload] #notifyGallery: no imageUrl available, skipping event');
      return;
    }

    const fileName = this.uploadedFile?.name || this.originalImageData?.fileName;
    const fileType = this.uploadedFile?.type || this.originalImageData?.fileType;
    const width = this.originalImageData?.width;
    const height = this.originalImageData?.height;

    const event = new CustomEvent('photo-upload:changed', {
      detail: {
        imageUrl: imageUrl,
        fileName: fileName,
        fileType: fileType,
        width: width,
        height: height
      },
      bubbles: true
    });

    console.log('[PhotoUpload] dispatching photo-upload:changed', { imageUrl, width, height });
    document.dispatchEvent(event);
  }

  #notifyGalleryRemoved() {
    const event = new CustomEvent('photo-upload:removed', {
      bubbles: true
    });

    document.dispatchEvent(event);
  }

  /**
   * Dynamically inject heic2any from CDN on first HEIC encounter.
   * De-duped at the class level — repeated calls return the same promise.
   */
  #loadHeic2AnyOnce() {
    if (typeof window.heic2any === 'function') return Promise.resolve();
    if (PhotoUpload._heic2anyPromise) return PhotoUpload._heic2anyPromise;

    PhotoUpload._heic2anyPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector('script[data-heic2any]');
      if (existing) {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () => reject(new Error('heic2any failed to load')));
        return;
      }
      const s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/heic2any@0.0.4/dist/heic2any.min.js';
      s.async = true;
      s.dataset.heic2any = 'true';
      s.onload = () => resolve();
      s.onerror = () => {
        PhotoUpload._heic2anyPromise = null;
        reject(new Error('heic2any failed to load'));
      };
      document.head.appendChild(s);
    });
    return PhotoUpload._heic2anyPromise;
  }

  async #convertHeicToJpeg(file) {
    try {
      // Lazy-load heic2any (~1.3MB) only when a HEIC file is encountered.
      // The vast majority of uploads are JPEG/PNG and never trigger this path.
      await this.#loadHeic2AnyOnce();

      const convertedBlob = await window.heic2any({
        blob: file,
        toType: 'image/jpeg',
        quality: 0.95
      });

      const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
      const fileName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
      const convertedFile = new File([blob], fileName, {
        type: 'image/jpeg',
        lastModified: Date.now()
      });

      return convertedFile;
    } catch (error) {
      console.error('[PhotoUpload] HEIC conversion error:', error);
      throw error;
    }
  }

  // 50c5: iPhone camera captures carry EXIF rotation in metadata, but the
  // raw bytes from FileReader.readAsDataURL are un-rotated. <img> respects
  // EXIF natively, but downstream canvas work loses the rotation, so the
  // mock renders sideways or fails outright.
  //
  // Bake the orientation in via createImageBitmap({ imageOrientation:
  // 'from-image' }) and re-encode as JPEG. Cap at 2048px so multi-MB
  // mobile captures don't blow out canvas memory. Falls back to the raw
  // FileReader path on browsers that don't support createImageBitmap with
  // imageOrientation (older Safari).
  async #readFileAsOrientedDataUrl(file) {
    if (typeof createImageBitmap !== 'function') {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    }
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
      const MAX = 2048;
      let { width, height } = bitmap;
      if (width > MAX || height > MAX) {
        const scale = MAX / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(bitmap, 0, 0, width, height);
      bitmap.close?.();
      return canvas.toDataURL('image/jpeg', 0.92);
    } catch (_e) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    }
  }

  async #loadAndStoreImage(file) {
    const dataUrl = await this.#readFileAsOrientedDataUrl(file);
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        this.originalImageData = {
          image: img,
          width: img.width,
          height: img.height,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type
        };
        this.#saveToSessionStorage(dataUrl, file.name, file.size, file.type, img.width, img.height);
        resolve();
      };
      img.onerror = reject;
      img.src = dataUrl;
    });
  }

  #getCurrentAspectRatio() {
    const skuPicker = window.skuPickerInstance;
    if (!skuPicker || !skuPicker.state || !skuPicker.data) {
      return null;
    }

    const sizeOption = skuPicker.state.confirmed?.size_option;
    if (!sizeOption) {
      return null;
    }

    const sizeData = skuPicker.data.options?.size?.find(s => s.handle === sizeOption);
    if (!sizeData || !sizeData.height_inches || !sizeData.width_inches) {
      return null;
    }

    const height = parseFloat(sizeData.height_inches);
    const width = parseFloat(sizeData.width_inches);

    return height / width;
  }

  async #cropToCurrentAspectRatio() {
    if (!this.originalImageData) return;

    const targetAspectRatio = this.#getCurrentAspectRatio();
    if (!targetAspectRatio) {
      this.uploadedImageURL = URL.createObjectURL(this.uploadedFile);
      this.croppedImageURL = null;
      return;
    }

    const img = this.originalImageData.image;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const sourceAspectRatio = img.height / img.width;
    let sourceX = 0;
    let sourceY = 0;
    let sourceWidth = img.width;
    let sourceHeight = img.height;

    if (sourceAspectRatio > targetAspectRatio) {
      sourceHeight = img.width * targetAspectRatio;
      sourceY = (img.height - sourceHeight) / 2;
    } else if (sourceAspectRatio < targetAspectRatio) {
      sourceWidth = img.height / targetAspectRatio;
      sourceX = (img.width - sourceWidth) / 2;
    }

    const maxDimension = 2400;
    let canvasWidth = sourceWidth;
    let canvasHeight = sourceHeight;

    if (canvasWidth > maxDimension || canvasHeight > maxDimension) {
      if (canvasWidth > canvasHeight) {
        canvasHeight = (maxDimension / canvasWidth) * canvasHeight;
        canvasWidth = maxDimension;
      } else {
        canvasWidth = (maxDimension / canvasHeight) * canvasWidth;
        canvasHeight = maxDimension;
      }
    }

    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    ctx.drawImage(
      img,
      sourceX, sourceY, sourceWidth, sourceHeight,
      0, 0, canvasWidth, canvasHeight
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          console.warn('[PhotoUpload] canvas.toBlob returned null, skipping crop');
          if (!this.uploadedImageURL && this.uploadedFile) {
            this.uploadedImageURL = URL.createObjectURL(this.uploadedFile);
          }
          resolve();
          return;
        }
        if (this.croppedImageURL) {
          URL.revokeObjectURL(this.croppedImageURL);
        }

        this.croppedImageURL = URL.createObjectURL(blob);
        resolve();
      }, 'image/jpeg', 0.92);
    });
  }

  async #handleSizeChange(event) {
    if (!this.originalImageData) return;

    const selection = event.detail.selection;
    const previousSize = this._lastSizeOption;
    const newSize = selection?.size_option;

    if (previousSize === newSize) return;

    this._lastSizeOption = newSize;

    await this.#cropToCurrentAspectRatio();
    if (!this.originalImageData) return;
    this.#notifyGallery();
  }

  async #dataURLToBlob(dataUrl) {
    const response = await fetch(dataUrl);
    return await response.blob();
  }

  #saveToSessionStorage(dataUrl, fileName, fileSize, fileType, width, height) {
    try {
      const photoData = {
        dataUrl: dataUrl,
        fileName: fileName,
        fileSize: fileSize,
        fileType: fileType,
        width: width,
        height: height,
        timestamp: Date.now()
      };
      sessionStorage.setItem('uploaded_photo', JSON.stringify(photoData));
    } catch (e) {
      console.warn('[PhotoUpload] Failed to save to sessionStorage:', e);
    }
  }

  async #restoreUploadedPhoto() {
    // Check URL parameter first (from cart link)
    const urlParams = new URLSearchParams(window.location.search);
    const photoUrlParam = urlParams.get('uploaded_photo');

    if (photoUrlParam) {
      try {
        let base64 = photoUrlParam.replace(/-/g, '+').replace(/_/g, '/');
        while (base64.length % 4) {
          base64 += '=';
        }
        let photoUrl = atob(base64);

        photoUrl = photoUrl.replace(/&amp;/g, '&')
                           .replace(/&lt;/g, '<')
                           .replace(/&gt;/g, '>')
                           .replace(/&quot;/g, '"')
                           .replace(/&#039;/g, "'");

        await this.#loadPhotoFromUrl(photoUrl);
        this.#removePhotoParamFromUrl();
        return;
      } catch (error) {
        console.warn('[PhotoUpload] Failed to load from URL param:', error);
      }
    }

    // Fallback to sessionStorage
    try {
      const stored = sessionStorage.getItem('uploaded_photo');
      if (!stored) return;

      const photoData = JSON.parse(stored);

      const oneHour = 60 * 60 * 1000;
      if (Date.now() - photoData.timestamp > oneHour) {
        sessionStorage.removeItem('uploaded_photo');
        return;
      }

      const img = new Image();
      img.onload = async () => {
        this.originalImageData = {
          image: img,
          width: photoData.width || img.width,
          height: photoData.height || img.height,
          fileName: photoData.fileName,
          fileSize: photoData.fileSize,
          fileType: photoData.fileType
        };

        const blob = await this.#dataURLToBlob(photoData.dataUrl);
        this.uploadedFile = new File([blob], photoData.fileName, { type: photoData.fileType });

        await this.#cropToCurrentAspectRatio();

        // Guard: photo may have been removed while crop was in-flight.
        // #removePhoto sets originalImageData to null — if that happened,
        // clearPhotoFromStorage already ran and we must not re-notify.
        if (!this.originalImageData) return;

        // Update uploaded state display
        this.#updateUploadedDisplay();

        // Show uploaded state
        this.#setState('uploaded');

        // Notify gallery
        this.#notifyGallery();

        // Enable add to cart
        this.#enableAddToCartButton();
      };
      img.src = photoData.dataUrl;
    } catch (e) {
      console.warn('[PhotoUpload] Failed to restore from sessionStorage:', e);
      sessionStorage.removeItem('uploaded_photo');
    }
  }

  async #loadPhotoFromUrl(url) {
    const img = new Image();

    await new Promise((resolve, reject) => {
      img.onload = () => {
        resolve();
      };
      img.onerror = (err) => {
        console.error('[PhotoUpload] Image failed to load:', err);
        reject(err);
      };
      img.src = url;
    });

    this.uploadedImageURL = url;
    this.croppedImageURL = url;
    this.originalImageData = {
      image: img,
      width: img.width,
      height: img.height,
      fileName: 'uploaded-photo.jpg',
      fileSize: 0,
      fileType: 'image/jpeg'
    };

    // Persist so #restoreUploadedPhoto can show the "uploaded" state on reload.
    // We store the URL directly in the dataUrl field — img.src and fetch() both
    // accept HTTP URLs, so the normal restore path works unchanged.
    this.#saveToSessionStorage(url, 'uploaded-photo.jpg', 0, 'image/jpeg', img.width, img.height);

    // Update uploaded state display
    this.#updateUploadedDisplay();

    // Show uploaded state
    this.#setState('uploaded');

    // Notify gallery
    this.#notifyGallery();

    // Enable add to cart
    this.#enableAddToCartButton();
  }

  #removePhotoParamFromUrl() {
    const url = new URL(window.location.href);
    url.searchParams.delete('uploaded_photo');
    window.history.replaceState({}, '', url.toString());
  }

  #formatMegapixels(pixels) {
    const megapixels = pixels / 1000000;
    if (megapixels >= 10) {
      return `${Math.round(megapixels)}MP`;
    } else {
      return `${megapixels.toFixed(1)}MP`;
    }
  }

  #enableAddToCartButton() {
    const addToCartButton = document.querySelector('button[data-photo-required]');
    if (addToCartButton) {
      addToCartButton.disabled = false;
      addToCartButton.removeAttribute('disabled');
    }
  }

  #disableAddToCartButton() {
    const addToCartButton = document.querySelector('button[data-photo-required]');
    if (addToCartButton) {
      addToCartButton.disabled = true;
      addToCartButton.setAttribute('disabled', 'true');
    }
  }

  // Public API for S3 integration.
  //
  // Idempotent per image (QA #108): the framed-print ATC handler used to block
  // ~15s on this call at submit time. It's now also kicked off eagerly when the
  // photo is added, so the upload runs in the background and Add-to-cart's await
  // resolves instantly. The in-flight/settled promise is cached keyed by the
  // current image URL so the eager call and the ATC await never upload twice;
  // a re-crop or new file changes the URL and re-uploads. Failures aren't
  // cached, so a later call retries.
  async uploadToS3() {
    if (!this.uploadedFile) {
      console.warn('[PhotoUpload] No file to upload');
      return null;
    }

    const imageUrl = this.croppedImageURL || this.uploadedImageURL;
    if (!imageUrl) {
      console.warn('[PhotoUpload] No image URL available');
      return null;
    }

    if (this._s3Promise && this._s3ImageUrl === imageUrl) {
      return this._s3Promise;
    }

    this._s3ImageUrl = imageUrl;
    this._s3Promise = this.#performUploadToS3(imageUrl).catch((error) => {
      // Drop the cache on failure so the next call (e.g. ATC) can retry.
      if (this._s3ImageUrl === imageUrl) {
        this._s3Promise = null;
        this._s3ImageUrl = null;
      }
      throw error;
    });
    return this._s3Promise;
  }

  async #performUploadToS3(imageUrl) {
    try {
      const blob = await fetch(imageUrl).then(r => r.blob());
      const file = new File([blob], this.uploadedFile.name, { type: 'image/jpeg' });

      const maxRetries = 2;
      let lastError = null;

      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          const s3Data = await this.#uploadToS3WithProgress(file, attempt);
          return s3Data;
        } catch (error) {
          lastError = error;
          console.warn(`[PhotoUpload] Upload attempt ${attempt} failed:`, error);

          if (attempt < maxRetries) {
            const delay = attempt * 1000;
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }
      }

      throw lastError;
    } catch (error) {
      console.error('[PhotoUpload] S3 upload failed:', error);
      throw error;
    }
  }

  async #uploadToS3WithProgress(file, attempt = 1) {
    this.#dispatchUploadEvent('started', { attempt });

    const response = await this.#uploadFileWithProgress(file);
    const data = JSON.parse(response);

    if (data.status !== 'success' || !data.key) {
      throw new Error('Upload failed or returned invalid response');
    }

    this.#dispatchUploadEvent('completed', {
      s3Key: data.key,
      s3Url: data.url || data.presignedUrl || data.cdn_url
    });

    return {
      key: data.key,
      url: data.url || data.presignedUrl || data.cdn_url
    };
  }

  #uploadFileWithProgress(file) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = Math.round((e.loaded / e.total) * 100);
          this.#dispatchUploadEvent('progress', {
            loaded: e.loaded,
            total: e.total,
            percent: percentComplete,
          });
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(xhr.responseText);
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Network error during upload'));
      });

      xhr.addEventListener('abort', () => {
        reject(new Error('Upload aborted'));
      });

      const formData = new FormData();
      formData.append('file', file);
      formData.append('filename', file.name);

      xhr.open('POST', '/apps/vespa-search/photos/upload');
      xhr.send(formData);
    });
  }

  #dispatchUploadEvent(status, detail = {}) {
    const event = new CustomEvent('photo-upload:s3-status', {
      detail: {
        status,
        ...detail,
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }

  updateFormPropertyWithS3Key(s3Data) {
    const form = document.querySelector('form[action*="/cart/add"]');
    if (!form) {
      console.warn('[PhotoUpload] Cart form not found');
      return;
    }

    const s3Key = typeof s3Data === 'string' ? s3Data : s3Data.key;
    const s3Url = typeof s3Data === 'object' ? s3Data.url : null;

    let keyInput = form.querySelector('input[name="properties[_artwork_s3_key]"]');
    if (!keyInput) {
      keyInput = document.createElement('input');
      keyInput.type = 'hidden';
      keyInput.name = 'properties[_artwork_s3_key]';
      keyInput.setAttribute('data-photo-upload-property', '');
      form.appendChild(keyInput);
    }
    keyInput.value = s3Key;

    if (s3Url) {
      let urlInput = form.querySelector('input[name="properties[_uploaded_photo_url]"]');
      if (!urlInput) {
        urlInput = document.createElement('input');
        urlInput.type = 'hidden';
        urlInput.name = 'properties[_uploaded_photo_url]';
        urlInput.setAttribute('data-photo-upload-property', '');
        form.appendChild(urlInput);
      }
      urlInput.value = s3Url;
    }
  }
}

// Register custom element
if (!customElements.get('photo-upload-component')) {
  customElements.define('photo-upload-component', PhotoUploadComponent);
}
